import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useHealth, buildSummary } from "@/context/HealthContext";
import { HCard, RiskBadge, Sparkline } from "@/components/health-kit";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Droplets, Scale, HeartPulse, Activity, FlaskConical, ClipboardCheck, RefreshCw, FileText } from "lucide-react";

export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { ht, refresh, tick } = useHealth();
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { api.get(`/health/patients/${id}`).then(({ data }) => setP(data)).catch(() => {}); }, [id]);
  useEffect(() => { load(); }, [load, tick]);

  if (!p) return <div className="p-10 text-center text-slate-400 font-mono text-sm">…</div>;
  const latest = p.analyses?.[0];
  const v = p.vitals || {};

  const runAnalysis = async () => {
    setBusy(true);
    try { await api.post(`/health/patients/${id}/analyze`); toast.success(ht("nav_aicore")); refresh(); load(); }
    catch { toast.error("Error"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 ngx-fade-in max-w-5xl" data-testid="patient-detail">
      <button onClick={() => navigate("/health/patients")} data-testid="patient-back" className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-100">
        <ArrowLeft className="h-4 w-4" /> {ht("nav_patients")}
      </button>

      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-display text-2xl font-bold text-slate-50 font-mono">{p.patient_id}</h1>
            {latest && <RiskBadge level={latest.risk_level} ht={ht} />}
          </div>
          <p className="text-sm text-slate-400 mt-1">{p.age} {ht("years")} · {p.sex} · {ht("cs_" + p.care_setting)}</p>
        </div>
        <Button onClick={runAnalysis} disabled={busy} data-testid="run-analysis" className="h-11 bg-teal-600 hover:bg-teal-700 text-white">
          <RefreshCw className={`h-4 w-4 mr-2 ${busy ? "animate-spin" : ""}`} /> {ht("runAnalysis")}
        </Button>
      </div>

      {latest && (
        <HCard className="p-5 border-teal-900/50">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-teal-400"><Activity className="h-4 w-4" /><span className="text-sm font-semibold">{ht("aiSummary")}</span></div>
            <span className="text-xs font-mono text-slate-500">{ht("confidence")}: {latest.confidence}%</span>
          </div>
          <p className="text-sm text-slate-200">{buildSummary(latest, ht)}</p>
          {latest.status === "pending_review" && (
            <Button onClick={() => navigate("/health/review")} data-testid="patient-goto-review" className="mt-4 h-9 bg-amber-600 hover:bg-amber-500 text-white text-sm">
              <ClipboardCheck className="h-4 w-4 mr-2" /> {ht("nav_review")}
            </Button>
          )}
        </HCard>
      )}

      {p.analyses?.length > 1 && (
        <HCard className="p-5">
          <div className="flex items-center gap-2 mb-3 text-slate-300"><Activity className="h-4 w-4 text-teal-400" /><h3 className="font-display font-semibold text-sm">{ht("aiAnalyses")}</h3></div>
          <div className="space-y-1.5" data-testid="patient-analyses-history">
            {p.analyses.map((a) => (
              <div key={a.id} className="flex items-center gap-3 text-xs font-mono px-3 py-2 rounded-sm bg-[#050C11] border border-slate-800">
                <span className="text-slate-500">{a.time}</span>
                <RiskBadge level={a.risk_level} ht={ht} />
                <span className="text-slate-500">{(a.signals || []).length} · {a.confidence}%</span>
                <span className="ml-auto text-slate-500">{a.status === "reviewed" ? ht("st_reviewed") : a.status === "pending_review" ? ht("st_requires_review") : a.status}</span>
              </div>
            ))}
          </div>
        </HCard>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <HCard className="p-5">
          <div className="flex items-center gap-2 mb-4 text-slate-300"><HeartPulse className="h-4 w-4 text-teal-400" /><h3 className="font-display font-semibold text-sm">{ht("vitals")}</h3></div>
          <div className="grid grid-cols-2 gap-3 font-mono text-sm">
            <Vital label="HR" value={`${v.hr} bpm`} /><Vital label="SpO₂" value={`${v.spo2}%`} />
            <Vital label="BP" value={`${v.bp_sys}/${v.bp_dia}`} /><Vital label="Temp" value={`${v.temp}°C`} />
          </div>
        </HCard>

        <HCard className="p-5">
          <div className="flex items-center gap-2 mb-4 text-slate-300"><FlaskConical className="h-4 w-4 text-teal-400" /><h3 className="font-display font-semibold text-sm">{ht("biomarkers")}</h3></div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-slate-200 font-mono">{p.biomarker?.name}</div>
              <div className="text-xs text-slate-500 font-mono">{p.biomarker?.trend?.[0]} → {p.biomarker?.trend?.at(-1)} {p.biomarker?.unit}</div>
            </div>
            <Sparkline values={p.biomarker?.trend} tone="#fb923c" />
          </div>
        </HCard>

        <HCard className="p-5">
          <div className="flex items-center gap-2 mb-4 text-slate-300"><Droplets className="h-4 w-4 text-teal-400" /><h3 className="font-display font-semibold text-sm">{ht("hydration")}</h3></div>
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-200 font-mono">{p.hydration_trend?.[0]}% → {p.hydration_trend?.at(-1)}%</div>
            <Sparkline values={p.hydration_trend} tone="#2dd4bf" />
          </div>
        </HCard>

        <HCard className="p-5">
          <div className="flex items-center gap-2 mb-4 text-slate-300"><Scale className="h-4 w-4 text-teal-400" /><h3 className="font-display font-semibold text-sm">{ht("weight")}</h3></div>
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-200 font-mono">{p.weight_trend?.[0]} → {p.weight_trend?.at(-1)} kg</div>
            <Sparkline values={p.weight_trend} tone="#818cf8" />
          </div>
        </HCard>
      </div>

      <HCard className="p-5">
        <div className="flex items-center gap-2 mb-3 text-slate-300"><FileText className="h-4 w-4 text-teal-400" /><h3 className="font-display font-semibold text-sm">{ht("notes")}</h3></div>
        {p.note_codes?.length ? (
          <div className="flex flex-wrap gap-2">
            {p.note_codes.map((n) => <span key={n} className="text-xs px-2.5 py-1 rounded-sm bg-[#050C11] border border-slate-700 text-slate-300">{ht("note_" + n)}</span>)}
          </div>
        ) : <span className="text-sm text-slate-500">{ht("noNotes")}</span>}
      </HCard>

      <HCard className="p-5">
        <h3 className="font-display font-semibold text-sm text-slate-300 mb-3">{ht("timeline")}</h3>
        <div className="space-y-2">
          {p.timeline?.map((t, i) => (
            <div key={i} className="flex items-center gap-3 text-sm">
              <span className="font-mono text-xs text-slate-500 w-16">{t.time}</span>
              <span className="h-2 w-2 rounded-full bg-teal-500" />
              <span className="text-slate-300">{ht("tl_" + t.code)}</span>
            </div>
          ))}
        </div>
      </HCard>
    </div>
  );
}

function Vital({ label, value }) {
  return (
    <div className="flex items-center justify-between px-3 py-2 rounded-sm bg-[#050C11] border border-slate-800">
      <span className="text-slate-500 text-xs">{label}</span><span className="text-slate-100">{value}</span>
    </div>
  );
}
